import { useState, useEffect, useRef } from 'react';

interface BackgroundMusicProps {
  autoStopOnScroll?: boolean;
}

const BackgroundMusic = ({ autoStopOnScroll = false }: BackgroundMusicProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hasAutoStopped, setHasAutoStopped] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Nhạc nền nhẹ nhàng, cảm động
  const musicUrl = 'https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3';

  // Tự động phát nhạc khi vào trang
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Thử phát nhạc ngay khi component mount
    const playAudio = async () => {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (error) {
        console.log('Autoplay prevented:', error);
        // Nếu autoplay bị chặn, thử lại khi user click bất kỳ đâu
        const handleFirstClick = async () => {
          try {
            await audio.play();
            setIsPlaying(true);
            document.removeEventListener('click', handleFirstClick);
          } catch (e) {
            console.log('Play error:', e);
          }
        };
        document.addEventListener('click', handleFirstClick, { once: true });
      }
    };

    playAudio();
  }, []);

  // Tự động tắt nhạc khi user click vào form đăng ký
  useEffect(() => {
    const handleFormFocus = () => {
      const audio = audioRef.current;
      if (!audio || hasAutoStopped) return;

      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        setHasAutoStopped(true);
      }
    };

    // Lắng nghe sự kiện focus vào các input trong form
    const formInputs = document.querySelectorAll('form[data-readdy-form] input');
    formInputs.forEach(input => {
      input.addEventListener('focus', handleFormFocus);
    });

    return () => {
      formInputs.forEach(input => {
        input.removeEventListener('focus', handleFormFocus);
      });
    };
  }, [isPlaying, hasAutoStopped]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
      setHasAutoStopped(false);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <>
      {/* Audio Element */}
      <audio
        ref={audioRef}
        src={musicUrl}
        loop
        preload="auto"
      />

      {/* Music Control Button - Fixed Bottom Left */}
      <div className="fixed bottom-6 left-6 z-50 flex flex-col gap-2">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 group"
          aria-label={isPlaying ? 'Pause music' : 'Play music'}
          title={isPlaying ? 'Tạm dừng nhạc' : 'Phát nhạc'}
        >
          {isPlaying ? (
            <i className="ri-pause-fill text-2xl"></i>
          ) : (
            <i className="ri-play-fill text-2xl"></i>
          )}
          
          {/* Ripple Effect khi đang phát */}
          {isPlaying && (
            <>
              <span className="absolute w-14 h-14 bg-teal-400 rounded-full animate-ping opacity-20"></span>
              <span className="absolute w-16 h-16 bg-teal-400 rounded-full animate-pulse opacity-10"></span>
            </>
          )}
        </button>

        {/* Mute/Unmute Button */}
        {isPlaying && (
          <button
            onClick={toggleMute}
            className="w-12 h-12 flex items-center justify-center bg-white text-teal-600 rounded-full shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            title={isMuted ? 'Bật tiếng' : 'Tắt tiếng'}
          >
            {isMuted ? (
              <i className="ri-volume-mute-fill text-xl"></i>
            ) : (
              <i className="ri-volume-up-fill text-xl"></i>
            )}
          </button>
        )}

        {/* Music Note Animation */}
        {isPlaying && !isMuted && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 pointer-events-none">
            <i className="ri-music-2-fill text-teal-500 text-xl animate-bounce"></i>
          </div>
        )}
      </div>
    </>
  );
};

export default BackgroundMusic;
